/**
 * LE PLAN DE L'HOTEL: trois niveaux servis par un ascenseur.
 *
 * L'hotel est le deuxieme modele a etage apres la villa, et le premier a en
 * porter DEUX. Le moteur n'a toujours aucune verticalite: chaque niveau est un
 * entier a cote de la position, on y marche en 2D sur toute l'emprise, et ce
 * qui change d'un niveau a l'autre, ce sont les murs en vigueur et la hauteur
 * de dessin. Rien de neuf — le mecanisme est celui de la terrasse de la villa,
 * simplement empile.
 *
 * CE QUE CHAQUE NIVEAU CONTIENT, et c'est la commande du proprietaire:
 *
 *   - Rez-de-chaussee: DEUX CHAMBRES avec leur lit, une a gauche, une a
 *     droite, jamais adjacentes — le hall les separe, et l'ascenseur est au
 *     milieu. Ce sont des CLOISONS INTERIEURES, le mecanisme du zoo.
 *   - Niveau 1: le BUREAU a gauche (petit bureau et portable, comme la villa)
 *     avec sa terrasse devant, et la SALLE A MANGER a droite, dont le mur du
 *     fond est une grande baie vitree qui donne sur le dehors.
 *   - Niveau 2: le SALON DE LECTURE a gauche — des livres et de quoi se
 *     detendre — et, sur le pont de droite, le BASSIN aux poissons, son
 *     palmier, et la cascade qui tombe le long de la facade.
 *
 * TOUS LES NOMBRES SONT DES FRACTIONS de l'emprise (15 x 12 m), et les marges
 * sont calculees, pas devinees. La regle qui commande tout: une bande de murs
 * ne doit JAMAIS toucher une autre bande, sinon les deux se renvoient le
 * bonhomme un tick sur deux — la secousse permanente deja payee sur la
 * terrasse de la villa et sur les enclos du zoo. Avec l'emprise de 15 x 12:
 *
 *   - au sol, l'enceinte (mur de 0,18 m) commence a pousser a 6,97 m du centre
 *     en x et 5,47 m en z; les chambres poussent au plus jusqu'a 6,80 m / 5,27 m;
 *   - a l'etage, l'acrotere (0,28 m) commence a 6,87 m / 5,37 m; les pieces
 *     poussent au plus jusqu'a 6,73 m / 5,27 m;
 *   - entre deux pieces d'un meme niveau, il reste toujours plus d'un metre de
 *     bande franche.
 *
 * tests/unit/hotel.test.ts ne fait pas confiance a ce calcul: il balaie toute
 * l'emprise, aux trois niveaux et sous les quatre rotations, et exige que
 * `resoudreMurs` soit un POINT FIXE.
 *
 * Module PUR: uniquement des donnees, pas de DOM, pas de hasard.
 */
import type { PieceEtage, PlanModeleEtage } from '../types/monde.ts'
import type { PieceInterieure } from './zoo.ts'

/**
 * LES CHAMBRES DU REZ-DE-CHAUSSEE — les cloisons interieures de l'hotel.
 *
 * Pas de drapeau `enclos`: ce sont des pieces ou l'on dort, pas des enclos du
 * zoo, et un animal familier conduit dans une chambre ne doit pas s'y faire
 * « installer ». Leur porte est sur leur face -z, vers le hall d'entree —
 * c'est la convention de toutes les ouvertures du projet.
 *
 * En metres: chaque chambre fait 4,2 x 4,56, centree a x = ±4,35, et le hall
 * fait 4,5 m de large entre les deux. Elles ne se touchent nulle part.
 */
const CHAMBRE_GAUCHE: PieceInterieure = { nom: 'Chambre de gauche', x: -0.29, z: 0.22, dl: 0.14, dp: 0.19, porte: 1.1 }
const CHAMBRE_DROITE: PieceInterieure = { nom: 'Chambre de droite', x: 0.29, z: 0.22, dl: 0.14, dp: 0.19, porte: 1.1 }

export const CHAMBRES_HOTEL: readonly PieceInterieure[] = Object.freeze([CHAMBRE_GAUCHE, CHAMBRE_DROITE])

/** Le bureau du niveau 1, sa terrasse devant lui. */
const BUREAU: PieceEtage = { x: -0.27, z: 0.14, dl: 0.155, dp: 0.27, porte: 1.2 }

/**
 * La salle a manger du niveau 1. Sa porte est plus large qu'une porte de
 * maison: on y entre a plusieurs, et le rendu y dessine la GRANDE BAIE du mur
 * du fond — le mur qu'on regarde en mangeant.
 */
const SALLE_A_MANGER: PieceEtage = { x: 0.27, z: 0.14, dl: 0.155, dp: 0.27, porte: 1.6 }

/** Le salon de lecture du niveau 2: les livres, un canape, la paix. */
const SALON_LECTURE: PieceEtage = { x: -0.26, z: 0.14, dl: 0.165, dp: 0.27, porte: 1.2 }

/**
 * LE BASSIN DU DERNIER NIVEAU, sur le pont de droite.
 *
 * Ce n'est ni une piscine ni un spa: on n'y nage pas et l'on ne s'y trempe pas
 * — des poissons y vivent. Il n'a donc AUCUN mur et ne repousse personne,
 * comme tout le mobilier; il n'existe que pour le rendu et pour les tests, qui
 * verifient qu'il reste sur le pont, hors du salon de lecture.
 */
export const ETANG_HOTEL: PieceEtage = { x: 0.3, z: 0.1, dl: 0.1, dp: 0.16, porte: 0 }

/** Le palmier du toit, plante a cote du bassin, sur le pont de droite. */
export const PALMIER_TOIT_HOTEL = { x: 0.3, z: -0.24 } as const

/**
 * LA CASCADE: elle deborde du bassin par la face +x LOCALE du batiment et
 * tombe le long de la facade jusqu'au sol. `z` est la fraction ou elle coule.
 */
export const CASCADE_HOTEL = { z: 0.1 } as const

/**
 * Le lit qu'on peut toucher: au centre de la chambre de droite — DERIVE de la
 * chambre, jamais recopie: deplacer la chambre deplace le lit avec elle.
 */
export const LIT_HOTEL = { x: CHAMBRE_DROITE.x, z: CHAMBRE_DROITE.z } as const

/** La table a manger: au centre de la salle, au niveau 1. Meme derivation. */
export const TABLE_HOTEL = { x: SALLE_A_MANGER.x, z: SALLE_A_MANGER.z } as const

/**
 * LE PLAN DES NIVEAUX, celui que `resoudreMurs` applique la-haut.
 *
 * L'ascenseur est la MEME colonne a tous les niveaux, dans le hall du milieu:
 * en bas entre les deux chambres, en haut entre les pieces — c'est ce qui rend
 * son debouche toujours degage, et les tests le verifient.
 */
export const PLAN_HOTEL: PlanModeleEtage = Object.freeze({
  acces: 'ascenseur',
  accesPos: { x: 0, z: 0.27 },
  niveaux: [
    [BUREAU, SALLE_A_MANGER],
    [SALON_LECTURE],
  ],
  // Le portable est sur le bureau du niveau 1, comme celui de la villa.
  portable: { x: -0.3, z: 0.3, niveau: 1 },
  // La table a manger est dressee au niveau 1: c'est ce que les cibles et
  // l'infobulle interrogent — jamais « le modele est-il l'hotel ? ».
  table: { ...TABLE_HOTEL, niveau: 1 },
  // Les boutons de l'ascenseur, et le message du HUD: texte montre au JOUEUR.
  nomsNiveaux: [
    'Rez-de-chaussée — les chambres',
    '1er étage — bureau et salle à manger',
    '2e étage — lecture et bassin',
  ],
})
